import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Tag } from "primereact/tag";
import { FaBell } from "react-icons/fa";
import CustomBreadcrumb from "../../components/CustomBreadcrumb";
import { getOneMeetingInfo } from "../../reducers/meetingSlice";
import { useParams } from "react-router-dom";

const ViewDetails = () => {
  const dispatch = useDispatch();
  const { id: meetingId } = useParams();
  const { currentMeeting, isLoading } = useSelector((state) => state.meeting);

  const [showDropdown, setShowDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (meetingId) dispatch(getOneMeetingInfo(meetingId));
  }, [dispatch, meetingId]);

  if (isLoading || !currentMeeting) return <div>Loading...</div>;

  const data = {
    title: currentMeeting.data.meetingName,
    date: currentMeeting.data.date,
    time: currentMeeting.data.time,
    organizer: currentMeeting.data.organizerName,
    reminderTime: currentMeeting.data.reminderTime,
    customers: Array.isArray(currentMeeting.data.customers)
      ? currentMeeting.data.customers.map((c) => ({
          name: c.name,
          number: c.phone,
          status: "confirmed", // mock status for now
        }))
      : [],
    templates: {
      booking: currentMeeting.data.bookingTemplate || null,
      reminder: currentMeeting.data.reminderTemplate || null,
      confirmation: currentMeeting.data.confirmationTemplate || null,
    },
  };

  const itemslist = [
    { label: "Meetings", url: "/meeting" },
    { label: "View Meeting", url: "/meeting/view" },
  ];

  const filteredCustomers = data.customers.filter((c) =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const confirmed = data.customers.filter(
    (c) => c.status === "confirmed"
  ).length;
  const pending = data.customers.filter((c) => c.status === "pending").length;
  const cancelled = data.customers.filter(
    (c) => c.status === "cancelled"
  ).length;

  const getStatusTag = (status) => {
    switch (status) {
      case "confirmed":
        return (
          <span style={{ color: "green", fontWeight: 600 }}>Confirmed</span>
        );
      case "pending":
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              backgroundColor: "#E7F0F7",
              padding: "4px 10px",
              borderRadius: "6px",
              color: "#FFA500",
              fontWeight: 500,
              fontSize: "0.85rem",
            }}
          >
            <FaBell size={12} /> <span>Pending</span>
          </div>
        );
      case "cancelled":
        return <span style={{ color: "red", fontWeight: 600 }}>Cancel</span>;
      default:
        return null;
    }
  };

  const getCustomerSummary = (customers) => {
    if (!Array.isArray(customers)) return "";
    const shown = customers.slice(0, 3).map((c) => c.name);
    const remaining = customers.length - 3;
    return remaining > 0
      ? `${shown.join(", ")}, +${remaining}`
      : shown.join(", ");
  };

  const cardStyle = {
    borderRadius: "10px",
    padding: "1.5rem",
    marginBottom: "1.5rem",
    boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
    backgroundColor: "#fff",
    border: "1px solid #e0e0e0",
    maxWidth: "800px",
  };

  const sectionTitleStyle = {
    fontSize: "1.2rem",
    fontWeight: "600",
    marginBottom: "1rem",
    color: "#333",
  };

  return (
    <div
      style={{
        padding: "2rem",
        backgroundColor: "#f8f9fa",
        minHeight: "100vh",
      }}
    >
      <CustomBreadcrumb
        home={{ label: "Meeting Scheduler", url: "/meeting" }}
        itemslist={itemslist}
        style={{ borderRadius: "8px", marginBottom: "1.5rem" }}
      />

      <h2
        style={{
          fontSize: "1.5rem",
          fontWeight: "bold",
          marginBottom: "1.5rem",
        }}
      >
        Meeting Details
      </h2>

      {/* 🧾 Meeting Info */}
      <div style={{ ...cardStyle, padding: "1.5rem" }}>
        <div style={{ marginBottom: "0.75rem" }}>
          <strong>Meeting Title:</strong> {data.title}
        </div>
        <div style={{ marginBottom: "0.75rem" }}>
          <strong>Date & Time:</strong> {data.date}, {data.time}
        </div>
        <div style={{ marginBottom: "0.75rem" }}>
          <strong>Organizer:</strong> {data.organizer}
        </div>
        <div style={{ marginBottom: "0.75rem" }}>
          <strong>Reminder Time:</strong> {data.reminderTime}
        </div>
      </div>

      {/* 👥 Customers */}
      <div style={cardStyle}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "1rem",
          }}
        >
          <div style={{ fontWeight: 600 }}>
            <i className="pi pi-users" /> Selected Customers (
            {data.customers.length})
          </div>
          <div style={{ fontSize: "0.85rem", color: "#666" }}>
            <span style={{ color: "green", marginRight: 10 }}>
              Confirmed: {confirmed}
            </span>
            <span style={{ color: "#c89600", marginRight: 10 }}>
              Pending: {pending}
            </span>
            <span style={{ color: "#d32f2f" }}>Cancelled: {cancelled}</span>
          </div>
        </div>

        {/* Dropdown */}
        <div
          onClick={() => setShowDropdown(!showDropdown)}
          style={{
            border: "1px solid #ccc",
            padding: "0.75rem 1rem",
            borderRadius: "6px",
            backgroundColor: "#fefefe",
            cursor: "pointer",
            marginBottom: "0.75rem",
          }}
        >
          <span>{getCustomerSummary(data.customers)}</span>
          <i
            className={`pi ${
              showDropdown ? "pi-chevron-up" : "pi-chevron-down"
            }`}
            style={{ float: "right" }}
          />
        </div>

        {showDropdown && (
          <div
            style={{
              border: "1px solid #ccc",
              padding: "1rem",
              borderRadius: "6px",
              backgroundColor: "#fff",
              maxHeight: "300px",
              overflowY: "auto",
            }}
          >
            <input
              type="text"
              placeholder="Search Customers"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: "100%",
                padding: "0.5rem",
                marginBottom: "1rem",
                borderRadius: "4px",
                border: "1px solid #ccc",
              }}
            />
            {filteredCustomers.map((c, idx) => (
              <div
                key={idx}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "0.5rem",
                  borderBottom: "1px solid #eee",
                }}
              >
                <div>
                  <div style={{ fontWeight: 600 }}>{c.name}</div>
                  <div style={{ fontSize: "0.85rem", color: "#777" }}>
                    {c.number}
                  </div>
                </div>
                <div>{getStatusTag(c.status)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 📩 Templates */}
      <div style={cardStyle}>
        <div style={sectionTitleStyle}>Selected Templates</div>

        {/* Booking Template */}
        <div style={{ marginBottom: "1rem" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              marginBottom: "5px",
            }}
          >
            <Tag severity="info" value="Booking Template" />
            <span style={{ fontSize: "0.9rem", color: "#888" }}>
              ID: {data.templates.booking?.id}
            </span>
          </div>
          <div
            style={{
              padding: "1rem",
              backgroundColor: "#f1f1f1",
              border: "1px solid #ccc",
              borderRadius: "8px",
              fontSize: "0.95rem",
              whiteSpace: "pre-line",
              color: "#444",
            }}
          >
            Params:{" "}
            {data.templates.booking?.templateComponents?.params?.join(", ")}
          </div>
        </div>

        {/* Reminder Template */}
        <div style={{ marginBottom: "1rem" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              marginBottom: "5px",
            }}
          >
            <Tag severity="warning" value="Reminder Template" />
            <span style={{ fontSize: "0.9rem", color: "#888" }}>
              ID: {data.templates.reminder?.id}
            </span>
          </div>
          <div
            style={{
              padding: "1rem",
              backgroundColor: "#f1f1f1",
              border: "1px solid #ccc",
              borderRadius: "8px",
              fontSize: "0.95rem",
              whiteSpace: "pre-line",
              color: "#444",
            }}
          >
            Params:{" "}
            {data.templates.reminder?.templateComponents?.params?.join(", ")}
          </div>
        </div>

        {/* Confirmation Template */}
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              marginBottom: "5px",
            }}
          >
            <Tag severity="success" value="Confirmation Template" />
            <span style={{ fontSize: "0.9rem", color: "#888" }}>
              ID: {data.templates.confirmation?.id}
            </span>
          </div>
          <div
            style={{
              padding: "1rem",
              backgroundColor: "#f1f1f1",
              border: "1px solid #ccc",
              borderRadius: "8px",
              fontSize: "0.95rem",
              whiteSpace: "pre-line",
              color: "#444",
            }}
          >
            Params:{" "}
            {data.templates.confirmation?.templateComponents?.params?.join(
              ", "
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewDetails;
